import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { Exact } from "@Helpers/types/exact";
import type { StatusResponse, ObjectIdString } from "@Helpers/schemas/common.schema";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import type {
  SaveProjectsRequest,
  SaveProjectsResponse,
  SaveProjectStatusRequest,
  SaveProjectStatusResponse,
  SaveProjectAssignedPositionResponse,
  GetAllStatusesForProjectsResponse,
} from "@ServerTypes/projects/project.schema";
import type {
  SaveTaskStatusRequest,
  SaveTaskStatusResponse,
  DeleteTaskRequest,
  DeleteTaskResponse,
  GetAllStatusesForTasksResponse,
} from "@ServerTypes/tasks/tasks.schema";
import type {
  GetPdfMarkUpDataRequest,
  GetPdfMarkUpDataResponse,
  GetIssuesIdToExportRequest,
  GetIssuesIdToExportResponse,
  SaveIssuesDataFromBCFRequest,
  SaveIssuesDataFromBCFResponse,
  GetIssuesDataRequest,
  GetIssuesDataResponse,
  IssueItem,
  GetIssuesPinDataRequest,
  GetIssuesPinDataResponse,
  GetIssueViewpointDataRequest,
  GetIssueViewpointDataResponse,
  GetIssuesDataByPortalRequest,
  GetIssuesDataByPortalResponse,
  RemoveIssueFromFormRequest,
  RemoveIssueFromFormResponse,
  AddFormIdToIssuesRequest,
  AddFormIdToIssuesResponse,
  UpdateIssueCustomFieldsRequest,
  UpdateIssueCustomFieldsResponse,
} from "@ServerTypes/issues/issues.schema";
import type { AxiosResponse } from "axios";
import type { StatusItem } from "@ServerTypes/status/status.schema";
import type { FetchDefaultListOfStatusesAvailableResponse } from "@ServerTypes/reviews/reviews.schema";
import type {
  GetCommentsRequest,
  GetCommentsResponse,
  SaveCommentsRequest,
  SaveCommentsResponse,
  DeleteCommentsRequest,
  DeleteCommentsResponse,
} from "@ServerTypes/comments/comments.schema.js";
import { defaultListOfStatusAvailableUrl } from "@Services/dataManager";
import { showToastNotification } from "@Reducers/toastSlice";
import { setIssuesCount } from "./issuesCount";
import { setTasksStatusesData } from "@Reducers/tasksStatuses";
import { setProjectsStatusesData } from "@Reducers/projectsStatuses";
import { setItemsHaveError, setItemsAreLoading } from "./bcfData";
import { setIssuesPinData } from "./issuesPinDataSlice";
import {
  updateIssuesData as arrangeIssuesData,
  setIssuesData,
  modifyIssuesData,
  setIssuesCommentsData,
  deleteIssuesCommentsData,
  mergeIssuesData as _mergeIssuesData,
  removeIssueFromState,
} from "./issues";
import { t, untranslated } from "@i18n";
import { EXPORT_TYPE, exportPdf } from "@Services/fileDownload";
import { errorCodes } from "@Helpers/errorCodes";

/** Row guard for the `unknown[]` the tasks-status endpoint declares; only `name` is required. */
function isStatusItem(value: unknown): value is StatusItem {
  return typeof value === "object" && value !== null && "name" in value && typeof value.name === "string";
}

interface ExportParams {
  projectId: ObjectIdString;
  issuesIds?: ObjectIdString[];
}

/**
 * Metadata-only update of an issue's standard/custom field answers from the detail sidebar.
 * The server validates only the isMetadata fields before persisting the edited formTemplate.
 */
export const updateIssueCustomFieldsRequest = (
  data: UpdateIssueCustomFieldsRequest,
): Promise<AxiosResponse<UpdateIssueCustomFieldsResponse>> =>
  axiosWrapper.post<UpdateIssueCustomFieldsResponse, UpdateIssueCustomFieldsRequest>(
    `${getBackendUrl()}/issues/updateIssueCustomFields`,
    data,
  );

export function removeIssueFromForm(
  ids: RemoveIssueFromFormRequest["ids"],
  formId: RemoveIssueFromFormRequest["formId"],
) {
  return async (dispatch: AppDispatch) => {
    const data: RemoveIssueFromFormRequest = { ids, formId };
    const response = await axiosWrapper.post<RemoveIssueFromFormResponse>(
      `${getBackendUrl()}/issues/removeIssueFromForm`,
      data,
    );
    if (response.data.status) {
      dispatch(removeIssueFromState({ ids }));
      dispatch(
        showToastNotification({
          content: t("All the selected Issue(s) were removed"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    return response.data.status;
  };
}

export function addFormIdToIssues(ids: AddFormIdToIssuesRequest["ids"], formId: AddFormIdToIssuesRequest["formId"]) {
  return async (dispatch: AppDispatch) => {
    const data: AddFormIdToIssuesRequest = { ids, formId };
    const response = await axiosWrapper.post<AddFormIdToIssuesResponse>(
      `${getBackendUrl()}/issues/addFormIdToIssues`,
      data,
    );
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("All the selected Issue(s) were added"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };
}

export function updateTaskData(data: Record<string, unknown>) {
  return {
    type: "UPDATE_TASK_DATA",
    data,
  };
}
export function removeTaskData(data: Record<string, unknown>) {
  return {
    type: "REMOVE_TASK_DATA",
    data,
  };
}
// `issueId: ObjectIdString` (not `Pick<…, "issueId">`) because the comments map in the issues slice
// is keyed by ONE issue id: the request schema's `ObjectIdString[]` arm has no reducer path here, so
// the single-id requirement belongs in this signature rather than in an assertion at the dispatch.
export function getCommentsData<T extends Exact<GetCommentsRequest, T>>(data: T & { issueId: ObjectIdString }) {
  return async (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    const responseData = await axiosWrapper.post<GetCommentsResponse>(
      `${getBackendUrl()}/comments/getCommentsData`,
      data,
    );
    if (responseData.data.status && responseData.data.data) {
      dispatch(
        setIssuesCommentsData({
          data: responseData.data.data,
          issueId: data.issueId,
        }),
      );
      return responseData.data.data;
    }
  };
}
export function saveCommentsData<T extends Exact<SaveCommentsRequest, T>>(
  data: T & Pick<SaveCommentsRequest, "issueId">,
) {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .post<SaveCommentsResponse>(`${getBackendUrl()}/comments/saveCommentsData`, data)
      .then((response) => {
        if (response.data.status && response.data.data) {
          dispatch(
            // `issueId` is optional on the request (a comment may target a Clash/Checklist instead),
            // but this reducer only exists for the issue branch, so the caller has supplied it.
            setIssuesCommentsData({ data: response.data.data, issueId: data.issueId!, isEdit: !!data.commentId }),
          );
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export function deleteCommentsData<T extends Exact<DeleteCommentsRequest, T>>(
  data: T & Pick<DeleteCommentsRequest, "issueId">,
) {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .post<DeleteCommentsResponse>(`${getBackendUrl()}/comments/deleteCommentsData`, data)
      .then((response) => {
        if (response.data.status) {
          // Same as above: `issueId` is optional on the request but required by the issues slice.
          dispatch(deleteIssuesCommentsData({ commentId: data.commentId, issueId: data.issueId! }));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export const issuesInFilesCount = async <T extends Exact<GetIssuesDataRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
  const result = response.data.result as unknown[] | undefined;
  return result?.length ?? 0;
};

export function getIssuesData<T extends Exact<GetIssuesDataRequest, T>>(
  data: T,
  returnValue = false,
  setIssueRequestState?: (state: { loading: boolean; error: null | number }) => void,
) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
    if (response.data.status && response.data.result) {
      dispatch(setIssuesData(response.data.result));
      if (typeof response.data.count === "number") {
        dispatch(setIssuesCount(response.data.count));
      }
    }
    setIssueRequestState &&
      setIssueRequestState({
        loading: false,
        error: !response.data.status ? response.data.errorCode || errorCodes.SOMETHING_WENT_WRONG : null,
      });
    if (returnValue) {
      return response.data.result;
    }
  };
}

export function fetchIssueById(issueId: NonNullable<IssueItem["id"]>) {
  return async (dispatch: AppDispatch): Promise<IssueItem | undefined> => {
    const result = await dispatch(getIssuesData({ issueId }, true));
    if (!Array.isArray(result)) return undefined;
    return result.find((item) => item.id === issueId) ?? result[0];
  };
}

export async function getIssuesList<T extends Exact<GetIssuesDataRequest, T>>(
  data: T,
): Promise<Record<string, unknown>[]> {
  const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
  // `result` is absent on a failed read, which is what the `?? []` covers — the old assertion sat in
  // front of it and made the fallback look dead. The declared `Record<string, unknown>[]` is the
  // open shape the sole consumer (issuesPdf) re-narrows to its own row type.
  return response.data.result ?? [];
}
export function getIssueViewpointData<T extends Exact<GetIssueViewpointDataRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssueViewpointDataResponse>(
      `${getBackendUrl()}/issues/getIssueViewpointData`,
      data,
    );
    if (response.data.status && response.data.result) {
      dispatch(modifyIssuesData(response.data.result));
    }
  };
}
export function getfilteredIssuesData<T extends Exact<GetIssuesDataRequest, T>>(data: T, returnValue = false) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
    if (response.data.status && response.data.result) {
      dispatch(arrangeIssuesData(response.data.result));
      if (typeof response.data.count === "number") {
        dispatch(setIssuesCount(response.data.count));
      }
    }
    if (returnValue) {
      return response.data.result;
    }
  };
}
export function getIssuesPinData<T extends Exact<GetIssuesPinDataRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssuesPinDataResponse>(
      `${getBackendUrl()}/issues/getIssuesPinData`,
      data,
    );
    if (response.data.status && response.data.result) {
      dispatch(setIssuesPinData(response.data.result));
    }
  };
}

export function getIssuesDataByPortal<T extends Exact<GetIssuesDataByPortalRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .post<GetIssuesDataByPortalResponse>(`${getBackendUrl()}/issues/getIssuesDataByPortal`, data)
      .then((response) => {
        if (response.data.result) {
          dispatch(setIssuesData(response.data.result));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export async function getCompleteIssuesDataByPortal<T extends Exact<GetIssuesDataByPortalRequest, T>>(data: T) {
  const response = await axiosWrapper.post<GetIssuesDataByPortalResponse>(
    `${getBackendUrl()}/issues/getIssuesDataByPortal`,
    data,
  );
  return response.data.result ?? [];
}
export function getTasksStatusesData() {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .get<GetAllStatusesForTasksResponse>(`${getBackendUrl()}/tasks/getAllStatusesForTasks`)
      .then((response) => {
        if (response.data.status) {
          // `getAllStatusesForTasksResponse.data` is `unknown[]` (its only producer is a stub that
          // always answers `[]`), so each row is checked rather than the array named by assertion.
          dispatch(setTasksStatusesData((response.data.data ?? []).filter(isStatusItem)));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export function getProjectsStatusesData() {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .get<GetAllStatusesForProjectsResponse>(`${getBackendUrl()}/projects/getAllStatusesForProjects`)
      .then((response) => {
        if (response.data.status) {
          dispatch(setProjectsStatusesData(response.data.data));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export function saveProjectStatus<T extends Exact<SaveProjectStatusRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    axiosWrapper
      .post<SaveProjectStatusResponse>(`${getBackendUrl()}/projects/saveProjectStatus`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(
            showToastNotification({
              content: t("Status changed successfully"),
            }),
          );
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export function saveProjectAttachedPositionData(data: Record<string, unknown>) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<SaveProjectAssignedPositionResponse>(
      `${getBackendUrl()}/projects/saveProjectAssignedPosition`,
      data,
    );
    let toastFields;
    if (response.data.status) {
      toastFields = {
        content: t("Assigned position changed successfully"),
        type: TOAST_TYPE_SUCCESS,
      };
    } else {
      toastFields = {
        content: untranslated(response.data.errorMessage || SOMETHING_WENT_WRONG),
        type: TOAST_TYPE_ERROR,
      };
    }
    dispatch(showToastNotification(toastFields));
  };
}
export function saveTaskStatus<T extends Exact<SaveTaskStatusRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    await axiosWrapper
      .post<SaveTaskStatusResponse>(`${getBackendUrl()}/tasks/saveTaskStatus`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(updateTaskData(data));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        Promise.reject(error);
      });
  };
}
export async function deleteTask<T extends Exact<DeleteTaskRequest, T>>(data: T) {
  const response = await axiosWrapper.post<DeleteTaskResponse>(`${getBackendUrl()}/tasks/deleteTask`, data);
  return response;
}
export async function saveProjectData<T extends Exact<SaveProjectsRequest, T>>(data: T): Promise<SaveProjectsResponse> {
  const response = await axiosWrapper.post<SaveProjectsResponse>(`${getBackendUrl()}/projects/saveProjectsData`, data);
  return response.data;
}
export async function getPdfMarkUpData<T extends Exact<GetPdfMarkUpDataRequest, T>>(data: T) {
  const response = await axiosWrapper.post<GetPdfMarkUpDataResponse>(
    `${getBackendUrl()}/issues/getPdfMarkUpData`,
    data,
  );
  return response;
}
export function successReadNotificationToast(selectedNotificationsId: string[]) {
  return async (dispatch: AppDispatch) => {
    const suffix = selectedNotificationsId.length > 1 ? "s" : "";
    dispatch(
      showToastNotification({
        content: t(`Notification${suffix} successfully marked as read`),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
  };
}
export async function getCommentsList<T extends Exact<GetCommentsRequest, T>>(
  data: T,
): Promise<{ data: GetCommentsResponse }> {
  return await axiosWrapper.post<GetCommentsResponse>(`${getBackendUrl()}/comments/getCommentsData`, data);
}
async function getIssuesIdToExport(data: GetIssuesIdToExportRequest) {
  return await axiosWrapper.post<GetIssuesIdToExportResponse>(`${getBackendUrl()}/issues/getIssuesIdToExport`, data);
}
export async function handleIssuesExportAsPDF({ projectId, issuesIds }: ExportParams): Promise<StatusResponse | void> {
  if (!issuesIds) {
    const res = await getIssuesIdToExport({ projectId });
    if (res.data.status) {
      issuesIds = res.data.result;
    } else {
      return res.data;
    }
  }
  if (issuesIds?.length) {
    exportPdf({ issuesIds, exportType: EXPORT_TYPE.PDF });
  } else {
    return {
      status: false,
      errorMessage: "There are no issues in this Project",
    };
  }
}
export const issuesBcfImport = async <T extends Exact<SaveIssuesDataFromBCFRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<SaveIssuesDataFromBCFResponse>(
    `${getBackendUrl()}/issues/saveIssuesDataFromBCF`,
    data,
  );
  return response;
};
export const getDefaultListOfStatusAvailable = async ({
  spaceId,
  entityType,
}: {
  spaceId: string;
  entityType: string;
}) => {
  const data = {
    value: {
      params: {
        entityType,
        spaceId,
      },
      entityType,
      spaceId,
    },
  };
  return await axiosWrapper.post<FetchDefaultListOfStatusesAvailableResponse>(defaultListOfStatusAvailableUrl, data);
};

// SCHEMA GAP: `userPermissions` is injected into each issue by the read aggregation
// (getUserPermissionPipeline) but is not declared on `issueItemShape`. Callers pass issue records of
// several shapes (redux `IssueItem`, grid rows, possibly `undefined`), so the parameter stays
// `unknown` and the permission array is narrowed at the read instead of being named by assertion.
export const isIssueRequiredPermission = (issuesData: unknown, requiredPermissionId: number): boolean => {
  if (typeof issuesData !== "object" || issuesData === null || !("userPermissions" in issuesData)) {
    return false;
  }
  const userPermissions: unknown = issuesData.userPermissions;
  return Array.isArray(userPermissions) && userPermissions.includes(requiredPermissionId);
};
